import { useState, useEffect } from 'react';
import { supabase } from '../services/supabase';
import { useAuth } from '../context/AuthContext';
import { Link } from 'react-router-dom';
import { Dialog, Transition } from '@headlessui/react';
import { Fragment } from 'react';
import { Listbox } from '@headlessui/react';
import { CheckIcon, ChevronDownIcon, ChevronUpIcon, ChevronUpDownIcon, DocumentArrowDownIcon } from '@heroicons/react/20/solid';
import { toast } from 'react-toastify';
import { generateJobsReport } from '../utils/jobsReportUtils';


function Jobs() {

document.title = 'Jobs';

  const { user } = useAuth();
  const [jobsData, setJobsData] = useState([]);
  const [filteredJobs, setFilteredJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isOpen, setIsOpen] = useState(false);
  const [poNumber, setPoNumber] = useState('');
  const [poDate, setPoDate] = useState('');
  const [lineItems, setLineItems] = useState([
    { part_number: '', description: '', quantity_ordered: '', unit_price: '', expected_delivery_date: '', searchTerm: '' }
  ]);
  const [parts, setParts] = useState([]);
  const [shops, setShops] = useState([]);
  const [filterStatus, setFilterStatus] = useState([]);
  const [filterShop, setFilterShop] = useState('');
  const [sortField, setSortField] = useState('');
  const [sortDirection, setSortDirection] = useState('asc');
  const [error, setError] = useState(null);
  const [isKanban, setIsKanban] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [excludeKanban, setExcludeKanban] = useState(false); // State for excluding Kanban jobs
  const [excludeClosed, setExcludeClosed] = useState(true); // State for excluding closed jobs
  const [isGeneratingReport, setIsGeneratingReport] = useState(false);



  useEffect(() => {
    const fetchData = async () => {
      if (!user) return;
  
      setLoading(true);
      try {
        const { data: jobs, error: jobsError } = await supabase
          .from('jobs')
          .select(`
            id, po_number, po_line_number, part_number, description, status, 
            quantity_on_order, produced_quantity, shipped_quantity, 
            expected_delivery_date, shops(id, name),
            parts!inner(part_number, part_number_master, revision, inventory_quantity)
          `)
          .not('expected_delivery_date', 'is', null)
          .order('po_number, po_line_number');
  
        if (jobsError) throw jobsError;
  
        const formattedJobs = jobs.map(job => ({
          id: job.id,
          po_number: job.po_number,
          po_line_number: job.po_line_number,
          part_number: job.part_number,
          description: job.description,
          status: job.status,
          quantity_on_order: job.quantity_on_order,
          produced_quantity: job.produced_quantity,
          shipped_quantity: job.shipped_quantity,
          remaining_quantity: job.quantity_on_order - job.shipped_quantity,
          expected_delivery_date: job.expected_delivery_date,
          shop_name: job.shops?.name || 'Unknown',
          inventory_quantity: job.parts?.inventory_quantity || 0,
        }));
  
        setJobsData(formattedJobs);
        handleFilter(formattedJobs); // Apply filter with fresh data
  
        const { data: partsData, error: partsError } = await supabase
          .from('parts')
          .select('part_number, part_number_master, revision, description');
        if (partsError) throw partsError;
        setParts(partsData || []);
  
        const { data: shopsData, error: shopsError } = await supabase
          .from('shops')
          .select('id, name');
        if (shopsError) throw shopsError;
        setShops(shopsData || []);
      } catch (err) {
        console.error('Error fetching data:', err);
        toast.error(`Failed to fetch jobs data: ${err.message}`);
      } finally {
        setLoading(false);
      }
    };
  
    fetchData();
  }, [user]);

  const addLineItem = () => {
    setLineItems([
      ...lineItems,
      { part_number: '', description: '', quantity_ordered: '', unit_price: '', expected_delivery_date: '', searchTerm: '' }
    ]);
  };

  const removeLineItem = (index) => {
    setLineItems(lineItems.filter((_, i) => i !== index));
  };

  // Updated to allow multiple field updates in one go
  const updateLineItem = (index, field, value) => {
    setLineItems(prev =>
      prev.map((item, i) =>
        i === index ? { ...item, [field]: value } : item
      )
    );
  };

  const updateLineItemMultiple = (index, updates) => {
    setLineItems(prev =>
      prev.map((item, i) =>
        i === index ? { ...item, ...updates } : item
      )
    );
  };

  const handlePartSelect = (index, part) => {
    updateLineItemMultiple(index, {
      part_number: part.part_number,
      description: part.description,
      searchTerm: ''
    });
  };

  const handleCreatePart = async (index) => {
    const searchTerm = lineItems[index].searchTerm;
    if (!searchTerm) return;
    const newPart = {
      part_number_master: searchTerm,
      revision: '0', // Default revision
      description: `New Part: ${searchTerm}`,
      inventory_quantity: 0,
    };
    try {
      const { data, error } = await supabase
        .from('parts')
        .insert(newPart)
        .select('part_number, description')
        .single();
      if (error) throw error;
      setParts([...parts, data]);
      handlePartSelect(index, data);
    } catch (err) {
      console.error('Error creating part:', err);
      setError('Failed to create new part: ' + err.message);
    }
  };

  const handleSavePO = async () => {
    if (
      !poNumber ||
      !poDate ||
      lineItems.some(
        item =>
          !item.part_number ||
          !item.quantity_ordered ||
          !item.unit_price ||
          (!isKanban && !item.expected_delivery_date)
      )
    ) {
      setError('Please fill in all required fields.');
      return;
    }
  
    try {
      // Fetch shop_id for each part in lineItems
      const partNumbers = lineItems.map(item => item.part_number);
      const { data: partsData, error: partsError } = await supabase
        .from('parts')
        .select('part_number, shop_id')
        .in('part_number', partNumbers);
      if (partsError) throw partsError;
  
      // Create a mapping of part_number to shop_id
      const shopIdMap = partsData.reduce((acc, part) => {
        acc[part.part_number] = part.shop_id || 1; // Default to 1 if shop_id is null
        return acc;
      }, {});
  
      // Create jobs array with the correct shop_id for each job
      const jobs = lineItems.map((item, index) => ({
        po_number: poNumber,
        po_line_number: index + 1,
        part_number: item.part_number,
        description: item.description,
        quantity_on_order: parseFloat(item.quantity_ordered),
        unit_price: parseFloat(item.unit_price),
        expected_delivery_date: isKanban ? null : item.expected_delivery_date,
        po_start_date: poDate,
        status: 'not_started',
        shop_id: shopIdMap[item.part_number] || 1, // Use the shop_id from parts, or default to 1
        customer_id: 1, // Default
        produced_quantity: 0,
        shipped_quantity: 0,
        estimated_running_days: 2,
      }));
  
      const { error } = await supabase.from('jobs').insert(jobs);
      if (error) throw error;
  
      // Refetch jobs data to update the UI
      const { data, error: fetchError } = await supabase
        .from('jobs')
        .select(`
          id, po_number, po_line_number, part_number, description, status, 
          quantity_on_order, produced_quantity, shipped_quantity, 
          expected_delivery_date, shops(id, name),
          parts!inner(part_number, inventory_quantity)
        `)
        .order('po_number, po_line_number');
      if (fetchError) throw fetchError;
  
      const formattedJobs = data.map(job => ({
        id: job.id,
        po_number: job.po_number,
        po_line_number: job.po_line_number,
        part_number: job.part_number,
        description: job.description,
        status: job.status,
        quantity_on_order: job.quantity_on_order,
        produced_quantity: job.produced_quantity,
        shipped_quantity: job.shipped_quantity,
        remaining_quantity: job.quantity_on_order - job.shipped_quantity,
        expected_delivery_date: job.expected_delivery_date,
        shop_name: job.shops?.name || 'Unknown',
        inventory_quantity: job.parts?.inventory_quantity || 0,
      }));
  
      setJobsData(formattedJobs);
      setFilteredJobs(formattedJobs);
  
      setIsOpen(false);
      setPoNumber('');
      setPoDate('');
      setIsKanban(false);
      setLineItems([
        { part_number: '', description: '', quantity_ordered: '', unit_price: '', expected_delivery_date: '', searchTerm: '' }
      ]);
      setError(null);
      toast.success('Purchase Order created successfully!');
    } catch (err) {
      console.error('Error saving PO:', err);
      setError('Failed to save PO: ' + err.message);
      toast.error(`Failed to save PO: ${err.message}`);
    }
  };

  const handleSort = (field) => {
    const newDirection = sortField === field && sortDirection === 'asc' ? 'desc' : 'asc';
    setSortField(field);
    setSortDirection(newDirection);

    const sortedJobs = [...filteredJobs].sort((a, b) => {
      const aValue = a[field] || '';
      const bValue = b[field] || '';
      if (field === 'expected_delivery_date') {
        const aDate = aValue ? new Date(aValue) : new Date(0);
        const bDate = bValue ? new Date(bValue) : new Date(0);
        return newDirection === 'asc' ? aDate - bDate : bDate - aDate;
      }
      if (typeof aValue === 'number')
        return newDirection === 'asc' ? aValue - bValue : bValue - aValue;
      return newDirection === 'asc'
        ? aValue.localeCompare(bValue)
        : bValue.localeCompare(aValue);
    });
    setFilteredJobs(sortedJobs);
  };

  const handleFilter = (data = jobsData) => {
    let filtered = [...data];
    if (filterStatus.length > 0) {
      filtered = filtered.filter(job => filterStatus.includes(job.status));
    }
    if (filterShop) {
      filtered = filtered.filter(job => job.shop_name === filterShop);
    }
    if (searchQuery) {
      filtered = filtered.filter(job =>
        job.po_number.toLowerCase().includes(searchQuery.toLowerCase()) ||
        job.part_number.toLowerCase().includes(searchQuery.toLowerCase())
      );
    }
    if (excludeKanban) {
      filtered = filtered.filter(job => job.expected_delivery_date !== null);
    }
    if (excludeClosed) {
      filtered = filtered.filter(job => job.status !== 'closed');
    }
    setFilteredJobs(filtered);
  };

  const handleGenerateReport = async () => {
    setIsGeneratingReport(true);
    try {
      // Use filtered jobs data for the report
      await generateJobsReport(filteredJobs);
      toast.success('Jobs report generated successfully!');
    } catch (error) {
      console.error('Error generating report:', error);
      toast.error(`Failed to generate report: ${error.message}`);
    } finally {
      setIsGeneratingReport(false);
    }
  };
  

  useEffect(() => {
    handleFilter();
  }, [filterStatus, filterShop, searchQuery, excludeKanban, excludeClosed]);
  

  const getStatusChipClass = (status) => {
    switch (status) {
      case 'not_started': return 'bg-red-100 text-red-800';
      case 'awaiting_production': return 'bg-orange-100 text-orange-800';
      case 'in_production': return 'bg-yellow-100 text-yellow-800';
      case 'in_progress': return 'bg-green-100 text-green-800';
      case 'partially_shipped': return 'bg-blue-100 text-blue-800';
      case 'ready_for_shipment': return 'bg-purple-100 text-purple-800';
      case 'closed': return 'bg-gray-100 text-gray-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  if (loading) return <div className="text-center text-gray-500 py-10">Loading...</div>;

  return (
    <div className="p-6 bg-gray-50 min-h-screen">
      <div className="w-full mx-auto">
      <div className="flex justify-between items-center mb-6">
  <h1 className="text-3xl font-bold text-gray-900">Jobs</h1>
  <div className="flex space-x-3">
    <button
      onClick={handleGenerateReport}
      disabled={isGeneratingReport}
      className="flex items-center px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition duration-200 disabled:bg-gray-400 disabled:cursor-not-allowed"
    >
      {isGeneratingReport ? (
        <>
          <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"></path>
          </svg>
          Generating...
        </>
      ) : (
        <>
          <DocumentArrowDownIcon className="w-5 h-5 mr-2" />
          Generate Report
        </>
      )}
    </button>
    <button
      onClick={() => setIsOpen(true)}
      className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition duration-200"
    >
      Create PO
    </button>
  </div>
</div>

        {/* Filters */}
        <div className="bg-white p-4 rounded-lg border-2 mb-6">
  <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
    <div>
      <label className="block text-sm font-medium text-gray-700">Search</label>
      <input
        type="text"
        value={searchQuery}
        onChange={(e) => setSearchQuery(e.target.value)}
        placeholder="Search by PO number or Part number"
        className="mt-1 block w-full p-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
      />
    </div>
    <div>
  <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
  <Listbox value={filterStatus} onChange={setFilterStatus} multiple>
    <div className="relative">
      <Listbox.Button className="relative w-full cursor-default rounded-lg border border-gray-300 bg-white py-2.5 pl-3 pr-10 text-left shadow-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 text-sm">
        <span className="block truncate">
          {filterStatus.length > 0 ? filterStatus.join(', ') : 'Select status'}
        </span>
        <span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2">
          <ChevronUpDownIcon className="h-5 w-5 text-gray-400" aria-hidden="true" />
        </span>
      </Listbox.Button>
      <Listbox.Options className="absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-md bg-white py-1 text-sm shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none">
        {[
          'not_started',
          'awaiting_production',
          'in_production',
          'in_progress',
          'partially_shipped',
          'ready_for_shipment',
          'closed'
        ].map((status) => (
          <Listbox.Option
            key={status}
            value={status}
            className={({ active }) =>
              `relative cursor-default select-none py-2 pl-10 pr-4 ${
                active ? 'bg-indigo-100 text-indigo-900' : 'text-gray-900'
              }`
            }
          >
            {({ selected }) => (
              <>
                <span
                  className={`block truncate ${
                    selected ? 'font-medium' : 'font-normal'
                  }`}
                >
                  {status.replace('_', ' ').replace(/\b\w/g, c => c.toUpperCase())}
                </span>
                {selected ? (
                  <span className="absolute inset-y-0 left-0 flex items-center pl-3 text-indigo-600">
                    <CheckIcon className="h-5 w-5" aria-hidden="true" />
                  </span>
                ) : null}
              </>
            )}
          </Listbox.Option>
        ))}
      </Listbox.Options>
    </div>
  </Listbox>
</div>

    <div>
      <label className="block text-sm font-medium text-gray-700">Shop</label>
      <select
        value={filterShop}
        onChange={(e) => setFilterShop(e.target.value)}
        className="mt-1 block w-full p-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
      >
        <option value="">All</option>
        {shops.map(shop => (
          <option key={shop.id} value={shop.name}>{shop.name}</option>
        ))}
      </select>
    </div>
    <div className='flex items-center gap-2'>
    <div className="flex items-center mt-6">
      <label className="inline-flex items-center">
        <input
          type="checkbox"
          checked={excludeClosed}
          onChange={(e) => setExcludeClosed(e.target.checked)}
          className="form-checkbox h-5 w-5 text-indigo-600 rounded"
        />
        <span className="ml-2 text-sm font-medium text-gray-700">Exclude Closed</span>
      </label>
    </div>
    </div>
  </div>
</div>

        {/* Jobs Table */}
        <div className="bg-white rounded-lg border-2 overflow-x-scroll">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer"
                  onClick={() => handleSort('po_number')}
                >
                  PO Number {sortField === 'po_number' && (sortDirection === 'asc' ? <ChevronUpIcon className="w-4 h-4 inline" /> : <ChevronDownIcon className="w-4 h-4 inline" />)}
                </th>
                <th
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer"
                  onClick={() => handleSort('expected_delivery_date')}
                >
                  Due {sortField === 'expected_delivery_date' && (sortDirection === 'asc' ? <ChevronUpIcon className="w-4 h-4 inline" /> : <ChevronDownIcon className="w-4 h-4 inline" />)}
                </th>
                <th
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer"
                  onClick={() => handleSort('po_line_number')}
                >
                  Line {sortField === 'po_line_number' && (sortDirection === 'asc' ? <ChevronUpIcon className="w-4 h-4 inline" /> : <ChevronDownIcon className="w-4 h-4 inline" />)}
                </th>
                <th
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer"
                  onClick={() => handleSort('part_number')}
                >
                  Part {sortField === 'part_number' && (sortDirection === 'asc' ? <ChevronUpIcon className="w-4 h-4 inline" /> : <ChevronDownIcon className="w-4 h-4 inline" />)}
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Description</th>
                <th
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer"
                  onClick={() => handleSort('status')}
                >
                  Status {sortField === 'status' && (sortDirection === 'asc' ? <ChevronUpIcon className="w-4 h-4 inline" /> : <ChevronDownIcon className="w-4 h-4 inline" />)}
                </th>
                <th
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer"
                  onClick={() => handleSort('quantity_on_order')}
                >
                  Order Qty {sortField === 'quantity_on_order' && (sortDirection === 'asc' ? <ChevronUpIcon className="w-4 h-4 inline" /> : <ChevronDownIcon className="w-4 h-4 inline" />)}
                </th>
                <th
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer"
                  onClick={() => handleSort('inventory_quantity')}
                >
                  Inventory {sortField === 'inventory_quantity' && (sortDirection === 'asc' ? <ChevronUpIcon className="w-4 h-4 inline" /> : <ChevronDownIcon className="w-4 h-4 inline" />)}
                </th>
                <th
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer"
                  onClick={() => handleSort('shipped_quantity')}
                >
                  Shipped {sortField === 'shipped_quantity' && (sortDirection === 'asc' ? <ChevronUpIcon className="w-4 h-4 inline" /> : <ChevronDownIcon className="w-4 h-4 inline" />)}
                </th>
                <th
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer"
                  onClick={() => handleSort('shop_name')}
                >
                  Shop {sortField === 'shop_name' && (sortDirection === 'asc' ? <ChevronUpIcon className="w-4 h-4 inline" /> : <ChevronDownIcon className="w-4 h-4 inline" />)}
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filteredJobs.map((job) => (
                <tr key={job.id} className="hover:bg-gray-50 transition duration-150">
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                    <Link to={`/jobs/${job.po_number}`} className="text-indigo-600 hover:text-indigo-800 transition duration-150">
                      {job.po_number}
                    </Link>
                  </td>
                  <td className={`px-6 py-4 whitespace-nowrap text-sm ${
  job.expected_delivery_date && new Date(job.expected_delivery_date) < new Date()
    ? 'text-red-600'
    : 'text-gray-900'
}`}>
  {job.expected_delivery_date ? new Date(job.expected_delivery_date).toLocaleDateString('en-US', { timeZone: 'UTC' }) : 'KANBAN'}
</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{job.po_line_number}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{job.part_number}</td>
                  <td
                        className="px-6 py-4 text-sm text-gray-900 truncate max-w-xs"
                        title={job.description}
                      >
                        {job.description?.length > 10
                          ? job.description.slice(0, 10) + '...'
                          : job.description}
                      </td>                  
                      <td className="px-6 py-4 whitespace-nowrap text-sm">
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusChipClass(job.status)}`}>
                      {job.status.replace('_', ' ').replace(/\b\w/g, char => char.toUpperCase())}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{job.quantity_on_order}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{job.inventory_quantity}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{job.shipped_quantity}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{job.shop_name}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Create PO Modal */}
        <Transition appear show={isOpen} as={Fragment}>
          <Dialog as="div" className="relative z-50" onClose={() => setIsOpen(false)}>
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0"
              enterTo="opacity-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100"
              leaveTo="opacity-0"
            >
              <div className="fixed inset-0 bg-gray-600 bg-opacity-50" />
            </Transition.Child>

            <div className="fixed inset-0 overflow-y-auto">
              <div className="flex min-h-full items-center justify-center p-4">
                <Transition.Child
                  as={Fragment}
                  enter="ease-out duration-300"
                  enterFrom="opacity-0 scale-95"
                  enterTo="opacity-100 scale-100"
                  leave="ease-in duration-200"
                  leaveFrom="opacity-100 scale-100"
                  leaveTo="opacity-0 scale-95"
                >
                  <Dialog.Panel className="w-full max-w-6xl transform overflow-hidden rounded-lg bg-white p-6 border-2 transition-all">
                    <Dialog.Title as="h2" className="text-xl font-semibold mb-4 text-gray-900">
                      Create Purchase Order
                    </Dialog.Title>

                    {error && <p className="mb-4 text-red-500">{error}</p>}

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                      <div>
                        <label className="block text-sm font-medium text-gray-700">PO Number</label>
                        <input
                          type="text"
                          value={poNumber}
                          onChange={(e) => setPoNumber(e.target.value)}
                          className="mt-1 block w-full p-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                          required
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700">PO Start</label>
                        <input
                          type="date"
                          value={poDate}
                          onChange={(e) => setPoDate(e.target.value)}
                          className="mt-1 block w-full p-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                          required
                        />
                      </div>
                    </div>

                    <div className="mb-4">
                      <label className="inline-flex items-center">
                        <input
                          type="checkbox"
                          checked={isKanban}
                          onChange={(e) => setIsKanban(e.target.checked)}
                          className="form-checkbox h-5 w-5 text-indigo-600 rounded"
                        />
                        <span className="ml-2 text-sm font-medium text-gray-700">Mark as Kanban (No Due Date)</span>
                      </label>
                    </div>

                    <div className="mb-6">
                      <h3 className="text-lg font-medium text-gray-900 mb-2">Line Items</h3>
                      <div className="overflow-x-auto">
                        <table className="min-w-full divide-y divide-gray-200 mb-20">
                          <thead className="bg-gray-50">
                            <tr>
                              <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Line #</th>
                              <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Part Number</th>
                              <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Description</th>
                              <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Qty Ordered</th>
                              <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Unit Price</th>
                              <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Due Date</th>
                              <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                            </tr>
                          </thead>
                          <tbody className="bg-white divide-y divide-gray-200">
                            {lineItems.map((item, index) => {
                              const filteredParts = parts.filter(part =>
                                part.part_number
                                  .toLowerCase()
                                  .includes((item.searchTerm || '').toLowerCase())
                              );
                              return (
                                <tr key={index} className="hover:bg-gray-50 transition duration-150 overflow-visible">
                                  <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-900">{index + 1}</td>
                                  <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-900 relative">
                                    <input
                                      type="text"
                                      value={item.searchTerm || item.part_number}
                                      onChange={(e) => {
                                        const value = e.target.value;
                                        updateLineItemMultiple(index, {
                                          searchTerm: value,
                                          // If the user types something different from the selected part, clear it out
                                          part_number: value !== item.part_number ? '' : item.part_number,
                                          description: value !== item.part_number ? '' : item.description,
                                        });
                                      }}
                                      className="block w-full p-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                                      placeholder="Search or enter part number"
                                    />
                                    {item.searchTerm && (
                                      <div className="absolute z-50 top-full left-0 mt-1 w-full bg-white border border-gray-300 rounded-lg max-h-60 overflow-y-auto">
                                        {filteredParts.length > 0 ? (
                                          filteredParts.map((part) => (
                                            <div
                                              key={part.part_number}
                                              onClick={() => handlePartSelect(index, part)}
                                              className="px-4 py-2 text-sm text-gray-900 hover:bg-indigo-100 cursor-pointer transition duration-150"
                                            >
                                              {part.part_number} - {part.description}
                                            </div>
                                          ))
                                        ) : (
                                          <div
                                            
                                            className="px-4 py-2 text-sm text-red-600 transition duration-150"
                                          >
                                            Part does not exist.
                                          </div>
                                        )}
                                      </div>
                                    )}
                                  </td>
                                  <td className="px-4 py-2 text-sm text-gray-900">
                                    <input
                                      type="text"
                                      value={item.description}
                                      onChange={(e) => updateLineItem(index, 'description', e.target.value)}
                                      className="block w-full p-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                                      placeholder="Description"
                                    />
                                  </td>
                                  <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-900">
                                    <input
                                      type="number"
                                      value={item.quantity_ordered}
                                      onChange={(e) => updateLineItem(index, 'quantity_ordered', e.target.value)}
                                      className="block w-full p-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                                      min="0"
                                      placeholder="Qty"
                                    />
                                  </td>
                                  <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-900">
                                    <input
                                      type="number"
                                      value={item.unit_price}
                                      onChange={(e) => updateLineItem(index, 'unit_price', e.target.value)}
                                      className="block w-full p-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                                      min="0"
                                      step="0.01"
                                      placeholder="Price"
                                    />
                                  </td>
                                  <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-900">
                                    <input
                                      type="date"
                                      value={item.expected_delivery_date}
                                      onChange={(e) => updateLineItem(index, 'expected_delivery_date', e.target.value)}
                                      className="block w-full p-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                                      required={!isKanban}
                                      disabled={isKanban}
                                    />
                                  </td>
                                  <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-900">
                                    <button
                                      onClick={() => removeLineItem(index)}
                                      className="text-red-600 hover:text-red-800 transition duration-150"
                                      disabled={lineItems.length === 1}
                                    >
                                      Remove
                                    </button>
                                  </td>
                                </tr>
                              );
                            })}
                            <tr></tr>
                          </tbody>
                        </table>
                      </div>

                      <button
                        onClick={addLineItem}
                        className="mt-4 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition duration-200"
                      >
                        Add Line Item
                      </button>
                    </div>

                    <div className="flex justify-end space-x-4">
                      <button
                        onClick={() => setIsOpen(false)}
                        className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition duration-200"
                      >
                        Cancel
                      </button>
                      <button
                        onClick={handleSavePO}
                        className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition duration-200"
                      >
                        Save PO
                      </button>
                    </div>
                  </Dialog.Panel>
                </Transition.Child>
              </div>
            </div>
          </Dialog>
        </Transition>
      </div>
    </div>
  );
}

export default Jobs;
